import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box, Typography, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, Paper, Chip, Divider
} from '@mui/material';
import {
  ReceiptText, FileText, Wallet, User, Store,
  ListChecks, Building2, Upload, Star,
  LogOut, Headphones, Zap
} from 'lucide-react';
import { UI_CONFIG } from '../../config/config';

const iconStyle = { fontSize: 20, flexShrink: 0 } as const;

const getIconComponent = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'Receipt': return <ReceiptText style={iconStyle} />;
    case 'Description': return <FileText style={iconStyle} />;
    case 'MonetizationOn': return <Wallet style={iconStyle} />;
    case 'Person': return <User style={iconStyle} />;
    case 'Store': return <Store style={iconStyle} />;
    case 'ListAlt': return <ListChecks style={iconStyle} />;
    case 'Business': return <Building2 style={iconStyle} />;
    case 'CloudUpload': return <Upload style={iconStyle} />;
    case 'Star': return <Star style={iconStyle} />;
    default: return <FileText style={iconStyle} />;
  }
};

interface SidebarProps {
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  width?: string;
  isMobile?: boolean;
  onItemClick?: () => void;
}

interface BusinessDetails {
  businessName?: string;
  businessId?: string;
  [key: string]: any;
}

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  new?: boolean;
  route?: string;
  onClick?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  onMouseEnter,
  onMouseLeave,
  width = '72px',
  isMobile = false,
  onItemClick,
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedBusiness, setSelectedBusiness] = useState<BusinessDetails | null>(null);
  const [businessDetails, setBusinessDetails] = useState<BusinessDetails | null>(null);

  const handleNavigate = (route?: string) => {
    if (route) {
      navigate(route);
    }
    if (onItemClick) {
      onItemClick();
    }
  };

  // Use menu items from config
  const menuItems: MenuItem[] = UI_CONFIG.MENU_ITEMS.map(item => ({
    text: item.text,
    icon: getIconComponent(item.icon),
    new: item.new,
    route: item.route,
    onClick: () => handleNavigate(item.route),
  }));

  // Determine active item from current route (exact match or child route)
  const isItemActive = (route?: string) => {
    if (!route) return false;
    const [base, query] = route.split('?');
    const onJourney = location.pathname === base || location.pathname.startsWith(`${base}/`);
    if (!onJourney) return false;
    if (query) {
      return location.search.includes(query);
    }
    return !location.search.includes('flow=');
  };

  // Determine if sidebar is expanded (always expanded in mobile drawer mode)
  // Any width wider than the 72px collapsed state is treated as expanded.
  const isExpanded = isMobile || parseInt(width, 10) > 100;

  return (
    <Paper
      className="dashboard-sidebar"
      onMouseEnter={isMobile ? undefined : onMouseEnter}
      onMouseLeave={isMobile ? undefined : onMouseLeave}
      elevation={0}
      sx={{
        borderRadius: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: 'linear-gradient(180deg, #0b2b4a 0%, #081f36 100%)',
        color: '#e2e8f0',
        width: isMobile ? '100%' : width,
        transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        height: isMobile ? '100%' : '100vh',
        position: isMobile ? 'relative' : 'sticky',
        top: 0,
        zIndex: 10,
        borderRight: '1px solid rgba(255, 255, 255, 0.08)',
        boxShadow: isMobile ? 'none' : '4px 0 24px rgba(11, 43, 74, 0.15)',
      }}
    >
      <Box
        sx={{
          position: 'absolute', width: 260, height: 260, borderRadius: '50%',
          top: -90, right: -110, pointerEvents: 'none',
          background: 'radial-gradient(circle, rgba(234, 179, 8, 0.16), transparent 70%)',
          filter: 'blur(34px)',
        }}
      />
      <Box
        sx={{
          position: 'absolute', width: 220, height: 220, borderRadius: '50%',
          bottom: 90, left: -120, pointerEvents: 'none',
          background: 'radial-gradient(circle, rgba(99, 102, 241, 0.16), transparent 70%)',
          filter: 'blur(36px)',
        }}
      />
      {/* Brand Header */}
      <Box
        sx={{
          p: 2,
          minHeight: 64,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          cursor: 'pointer',
        }}
        onClick={() => handleNavigate('/dashboard')}
        className="sidebar-header"
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            position: 'relative',
            background: 'linear-gradient(135deg, #eab308, #f59e0b)',
            color: '#0b2b4a',
            boxShadow: '0 6px 18px -4px rgba(234, 179, 8, 0.65)',
          }}
        >
          <ReceiptText size={22} style={{ position: 'relative', zIndex: 1 }} />
          <Box
            sx={{
              position: 'absolute', inset: -4, borderRadius: 3,
              background: 'radial-gradient(circle, rgba(234, 179, 8, 0.35), transparent 70%)',
              filter: 'blur(6px)',
              zIndex: 0,
            }}
          />
        </Box>
        <Box sx={{ opacity: isExpanded ? 1 : 0, transition: 'opacity 0.2s ease', overflow: 'hidden' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#ffffff', lineHeight: 1.2, fontSize: '1.1rem' }}>
            Invokta <span style={{ fontWeight: 400, color: '#eab308', fontSize: '0.75rem' }}>Billing</span>
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: '#94a3b8',
              fontSize: '0.7rem',
              display: 'block',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            {businessDetails?.businessName || selectedBusiness?.businessName || 'Business Suite'}
          </Typography>
        </Box>
      </Box>

      {/* Nav List */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          py: 1.5,
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px' },
        }}
      >
        <List sx={{ p: 1 }}>
          {menuItems.map((item, index) => {
            const active = isItemActive(item.route);
            return (
              <ListItem
                key={index}
                disablePadding
                className={`dashboard-sidebar-item${active ? ' active' : ''}`}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                }}
              >
                <ListItemButton
                  onClick={item.onClick}
                  sx={{
                    borderRadius: 2,
                    px: 1.75,
                    py: 1,
                    minHeight: 44,
                    backgroundImage: active
                      ? 'linear-gradient(90deg, rgba(234, 179, 8, 0.28) 0%, rgba(234, 179, 8, 0.08) 100%)'
                      : 'none',
                    backgroundColor: 'transparent',
                    borderLeft: active ? '3px solid #eab308' : '3px solid transparent',
                    color: active ? '#ffffff' : '#94a3b8',
                    transition: 'all 0.15s ease',
                    boxShadow: active ? 'inset 0 0 18px rgba(234, 179, 8, 0.12)' : 'none',
                    '&:hover': {
                      backgroundColor: active ? 'rgba(234, 179, 8, 0.2)' : 'rgba(255, 255, 255, 0.06)',
                      color: '#ffffff',
                    },
                  }}
                >
                  <ListItemIcon
                    className="dashboard-sidebar-icon"
                    sx={{
                      minWidth: 36,
                      color: active ? '#eab308' : '#94a3b8',
                      transition: 'color 0.15s ease, transform 0.15s ease',
                      '& svg': { transition: 'transform 0.15s ease' },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    sx={{
                      m: 0,
                      '& .MuiTypography-root': {
                        transition: 'opacity 0.2s ease',
                        opacity: isExpanded ? 1 : 0,
                        fontSize: '0.875rem',
                        fontWeight: active ? 600 : 500,
                        whiteSpace: 'nowrap',
                      },
                    }}
                    className="dashboard-sidebar-text"
                  />
                  {item.new && (
                    <Chip
                      label="New"
                      size="small"
                      sx={{
                        ml: 1,
                        height: 18,
                        fontSize: '0.625rem',
                        fontWeight: 700,
                        bgcolor: active ? '#eab308' : 'rgba(234, 179, 8, 0.2)',
                        color: active ? '#0b2b4a' : '#eab308',
                        transition: 'opacity 0.2s ease',
                        opacity: isExpanded ? 1 : 0,
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* Footer Support / Logout */}
      {/* Pro plan promo */}
      <Box
        sx={{
          mx: 1.5,
          mb: 1,
          p: 1.5,
          borderRadius: 2.5,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.22), rgba(99, 102, 241, 0.18))',
          border: '1px solid rgba(234, 179, 8, 0.35)',
          position: 'relative',
          cursor: 'pointer',
          opacity: isExpanded ? 1 : 0,
          transition: 'opacity 0.2s ease, border-color 0.2s ease',
          '&:hover': { borderColor: 'rgba(234, 179, 8, 0.7)' },
        }}
        onClick={isExpanded ? () => handleNavigate('/upgrade/ocr') : undefined}
      >
        <Box
          sx={{
            position: 'absolute', width: 70, height: 70, borderRadius: '50%',
            top: -30, right: -20,
            background: 'radial-gradient(circle, rgba(234, 179, 8, 0.5), transparent 70%)',
            filter: 'blur(10px)',
          }}
        />
        <Box sx={{ position: 'relative', zIndex: 1, opacity: isExpanded ? 1 : 0 }}>
          <Zap size={16} style={{ color: '#eab308' }} />
          <Typography variant="caption" sx={{ color: '#eab308', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', ml: 0.5 }}>
            OCR Premium
          </Typography>
          <Typography variant="caption" sx={{ color: '#cbd5e1', display: 'block', fontSize: '0.68rem' }}>
            Unlock unlimited OCR & GST-ready exports.
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)', mx: 1.5 }} />
      <Box sx={{ p: 1, pb: 1.5, overflow: 'hidden', whiteSpace: 'nowrap' }}>
        <ListItem disablePadding sx={{ borderRadius: 2, mb: 0.5 }}>
          <ListItemButton
            onClick={() => handleNavigate('/support')}
            sx={{
              borderRadius: 2,
              px: 1.75,
              py: 1,
              color: '#94a3b8',
              '&:hover': {
                backgroundColor: 'rgba(234, 179, 8, 0.1)',
                color: '#eab308',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
              <Headphones style={iconStyle} />
            </ListItemIcon>
            <ListItemText
              primary="Support"
              sx={{
                m: 0,
                '& .MuiTypography-root': {
                  opacity: isExpanded ? 1 : 0,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  transition: 'opacity 0.2s ease',
                },
              }}
            />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding sx={{ borderRadius: 2 }}>
          <ListItemButton
            onClick={async () => {
              await logout();
              navigate('/login');
            }}
            sx={{
              borderRadius: 2,
              px: 1.75,
              py: 1,
              color: '#94a3b8',
              '&:hover': {
                backgroundColor: 'rgba(239, 68, 68, 0.12)',
                color: '#f87171',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
              <LogOut style={iconStyle} />
            </ListItemIcon>
            <ListItemText
              primary="Logout"
              sx={{
                m: 0,
                '& .MuiTypography-root': {
                  opacity: isExpanded ? 1 : 0,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  transition: 'opacity 0.2s ease',
                },
              }}
            />
          </ListItemButton>
        </ListItem>
      </Box>
    </Paper>
  );
};

export default Sidebar;